import { Box, Popover } from '@mui/material';
import DOMPurify from 'dompurify';
import type { KeyboardEvent, MouseEvent } from 'react';
import React, { useContext, useMemo, useState } from 'react';
import {
    MATERIAL_REFERENCE_ATTRIBUTE,
    MATERIAL_REFERENCE_ID_ATTRIBUTE,
    MATERIAL_REFERENCE_LABEL_ATTRIBUTE,
    MATERIAL_REFERENCE_MIME_TYPE_ATTRIBUTE,
    MATERIAL_REFERENCE_TYPE_ATTRIBUTE,
    SPACE_KEYS
} from '../../lexical/lexicalConstants';
import { UserContext } from '../../auth/UserContext';
import { fetchAttachmentDownloadUrl } from '../../components/attachments';

const ACTIVATION_KEYS = new Set(['Enter', ...Array.from(SPACE_KEYS)]);
const ATTACHMENT_REFERENCE_SELECTOR = `[${MATERIAL_REFERENCE_ATTRIBUTE}]`;
const SANITIZED_RICH_TEXT_ATTRIBUTES = [
    'class',
    'target',
    MATERIAL_REFERENCE_ATTRIBUTE,
    MATERIAL_REFERENCE_ID_ATTRIBUTE,
    MATERIAL_REFERENCE_TYPE_ATTRIBUTE,
    MATERIAL_REFERENCE_LABEL_ATTRIBUTE,
    MATERIAL_REFERENCE_MIME_TYPE_ATTRIBUTE,
    'role',
    'tabindex'
];

type RichTextDisplayCellProps = {
    value?: string;
    clampLines?: number;
};

type RichTextInteractionEvent = MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>;

const RICH_CONTENT_SX = {
    fontSize: '0.875rem',
    wordBreak: 'break-word',
    '& p': { m: 0 },
    '& ul, & ol': { m: 0, pl: '1.5em' },
    '& h1, & h2, & h3, & h4, & h5, & h6': { m: 0 },
    '& blockquote': { m: 0 },
    '& table': { borderCollapse: 'collapse' },
    '& td, & th': { border: '1px solid', borderColor: 'divider', p: 0.5 },
    '& [data-showrunnr-mention]': {
        backgroundColor: 'rgba(24, 119, 232, 0.2)',
        border: '1px solid grey',
        borderRadius: '4px',
        px: '4px',
        display: 'inline-block'
    }
};

const isActivationKey = (key: string) => ACTIVATION_KEYS.has(key);

const getEventElement = (event: RichTextInteractionEvent): Element | null => {
    const target = event.target;
    return target instanceof Element ? target : target instanceof Text ? target.parentElement : null;
};

const getEventAnchor = (event: RichTextInteractionEvent): HTMLAnchorElement | null => {
    const element = getEventElement(event);
    const closestAnchor = element?.closest('a') ?? null;
    return closestAnchor instanceof HTMLAnchorElement ? closestAnchor : null;
};

const getEventAttachmentReference = (event: RichTextInteractionEvent): HTMLElement | null => {
    const element = getEventElement(event);
    return (element?.closest(ATTACHMENT_REFERENCE_SELECTOR) as HTMLElement | null) ?? null;
};

/**
 * Read-only grid cell renderer for rich text (HTML) content.
 *
 * Renders sanitized HTML clamped to `clampLines` (mirroring the EllipsisText
 * pattern) and opens a popover with the full rich content on click. It
 * intentionally does not mount a Lexical instance per cell so that grid
 * scrolling performance is unaffected.
 */
export default function RichTextDisplayCell({ value = '', clampLines = 1 }: RichTextDisplayCellProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { activeOrganizationAccount } = useContext(UserContext);

    const sanitizedHtml = useMemo(
        () =>
            value
                ? DOMPurify.sanitize(value, {
                      ADD_ATTR: SANITIZED_RICH_TEXT_ATTRIBUTES
                  })
                : '',
        [value]
    );

    if (!sanitizedHtml) {
        return null;
    }

    const handleMouseDown = (event: MouseEvent<HTMLElement>) => {
        const anchor = getEventAnchor(event);
        if (anchor?.href || getEventAttachmentReference(event)) {
            // Prevent the grid from stealing the first click to focus the cell.
            event.stopPropagation();
        }
    };

    const openReferenceElement = async (referenceElement: HTMLElement | null) => {
        if (!referenceElement) {
            return false;
        }

        const referenceId = referenceElement.getAttribute(MATERIAL_REFERENCE_ID_ATTRIBUTE);

        if (referenceId) {
            try {
                const attachmentUrl = await fetchAttachmentDownloadUrl(referenceId, activeOrganizationAccount);
                if (attachmentUrl) {
                    window.open(attachmentUrl, '_blank', 'noopener,noreferrer');
                }
            } catch (error) {
                // TODO: Replace this with a user-facing toast once the failure UX is finalized.
                console.error('Failed to open attachment reference', error);
            }
            return true;
        }

        return false;
    };

    const handleAttachmentReferenceClick = (event: RichTextInteractionEvent) => {
        const attachmentReference = getEventAttachmentReference(event);
        if (!attachmentReference) {
            return false;
        }

        event.preventDefault();
        event.stopPropagation();
        void openReferenceElement(attachmentReference);
        return true;
    };

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (handleAttachmentReferenceClick(event)) {
            return;
        }

        if (getEventAnchor(event)?.href) {
            event.stopPropagation();
            return;
        }

        setAnchorEl(event.currentTarget);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (!isActivationKey(event.key)) {
            return;
        }

        if (handleAttachmentReferenceClick(event)) {
            return;
        }

        if (getEventAnchor(event)?.href) {
            return;
        }

        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (isActivationKey(event.key)) {
            handleAttachmentReferenceClick(event);
        }
    };

    const handleClose = (event: unknown) => {
        if (
            event &&
            typeof event === 'object' &&
            'stopPropagation' in event &&
            typeof event.stopPropagation === 'function'
        ) {
            event.stopPropagation();
        }
        setAnchorEl(null);
    };

    return (
        <>
            <Box
                className="clickable clamp"
                role="button"
                tabIndex={0}
                aria-haspopup="dialog"
                aria-expanded={Boolean(anchorEl)}
                onMouseDown={handleMouseDown}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                sx={{
                    display: '-webkit-box',
                    '--clamp-lines': clampLines,
                    width: '100%',
                    ...RICH_CONTENT_SX
                }}
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
            >
                <Box
                    sx={{ maxWidth: '400px', overflow: 'auto', p: 2, ...RICH_CONTENT_SX }}
                    onClick={(event: MouseEvent<HTMLElement>) => {
                        handleAttachmentReferenceClick(event);
                    }}
                    onKeyDown={handlePopoverKeyDown}
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
            </Popover>
        </>
    );
}
